// Инфраструктура локализации, не покрытая файлами переводов.
//
// 1. Даты. moment.locale переключался только для ru/en («если не русский —
//    en-gb»), поэтому под польской и остальными локалями месяцы показывались
//    по-английски (аналитика, объекты, ai-чат) — при том, что календарь
//    Vuetify рядом уже говорил по-польски.
//
// 2. Статусы броней. В базе они лежат русскими строками («Новая», «Активная»),
//    и часть компонентов рендерила их сырыми. Карта «русское значение → ключ
//    локали» жила приватно в BookingHeader — теперь она здесь, одна на всех.

import moment from "moment";
// Локали moment подключаются явно — так видно, какие языки поддержаны. Бандл они
// не режут: IgnorePlugin для moment/locale в конфиге нет, webpack тянет контекст
// целиком; импорт остаётся документацией намерения и страховкой на случай, когда
// такой плагин появится.
import "moment/locale/ru";
import "moment/locale/en-gb";
import "moment/locale/de";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/el";
import "moment/locale/hr";
import "moment/locale/id";
import "moment/locale/it";
import "moment/locale/pl";
import "moment/locale/pt";
import "moment/locale/pt-br";
import "moment/locale/ro";
import "moment/locale/sr";
import "moment/locale/th";
import "moment/locale/tr";

// Код локали moment для локали приложения (src/locales/*).
const MOMENT_LOCALES = {
  ru: "ru",
  en: "en-gb",
  de: "de",
  es: "es",
  fr: "fr",
  el: "el",
  hr: "hr",
  id: "id",
  it: "it",
  pl: "pl",
  pt: "pt",
  "pt-BR": "pt-br",
  ro: "ro",
  sr: "sr", // наш sr — латиница; у moment "sr" тоже латиница (кириллица — sr-cyrl)
  th: "th",
  tr: "tr",
};

// Выбор локали приложения по тегу браузера ("pt-BR", "chr-US").
//
// Сравнение идёт по тегу целиком и по его первой части, а НЕ по вхождению
// подстроки, как было раньше: подстрочный поиск выдавал чероки (chr-US) за
// хорватский, а белорусский (bel-BY) и телугу (tel-IN) — за греческий, потому
// что коды «hr» и «el» лежат внутри этих тегов. Регистр не учитывается: старые
// Android-браузеры отдают «pt-br», а файл называется pt-BR.json. Полный тег
// проверяется раньше первой части, иначе «pt-BR» досталась бы локаль «pt».
export function localeForBrowserTag(tag, codes) {
  if (!tag || typeof tag !== "string" || !Array.isArray(codes)) return null;
  const normalized = tag.trim().toLowerCase();
  if (!normalized) return null;
  const exact = codes.find((code) => String(code).toLowerCase() === normalized);
  if (exact) return exact;
  const primary = normalized.split("-")[0];
  return codes.find((code) => String(code).toLowerCase() === primary) || null;
}

/**
 * Сохранённый язык, если он всё ещё существует в проекте. Локаль могли удалить
 * (так ушёл хинди) — тогда $i18n.locale указывал бы на несуществующий пак:
 * тексты падали бы на fallbackLocale, а moment и Vuetify получали бы
 * неизвестный код. Возвращает null, и вызывающая сторона чистит хранилище.
 */
export function validStoredLocale(stored, codes) {
  if (!stored || typeof stored !== "string" || !Array.isArray(codes)) return null;
  return codes.includes(stored) ? stored : null;
}

/**
 * Код локали, пришедший извне (профиль пользователя с сервера, query-параметр
 * ссылки), приведённый к тому, что в проекте есть. Возвращает null, если такой
 * локали нет: подставлять её в $i18n.locale нельзя — vue-i18n оставит код как
 * есть, тексты уйдут на fallback, а селектор языка покажет пустое значение.
 * Именно так вёл бы себя каждый вход пользователя, у которого на сервере
 * сохранён удалённый хинди.
 */
export function supportedLocale(candidate, codes) {
  if (!candidate || typeof candidate !== "string" || !Array.isArray(codes)) return null;
  if (codes.includes(candidate)) return candidate;
  return localeForBrowserTag(candidate, codes);
}

export function momentLocaleFor(appLocale) {
  return MOMENT_LOCALES[appLocale] || "en-gb";
}

/** Единая точка переключения языка дат — вместо if ru/else en-gb по компонентам. */
export function setMomentLocale(appLocale) {
  moment.locale(momentLocaleFor(appLocale));
}

// Статусы броней: значение из БД (по-русски) → ключ локали. Ровно те же пары,
// что исторически были в BookingHeader.getStateTooltip.
const BOOKING_STATE_KEYS = {
  "Новая": "bookings_states.new",
  "В обработке": "bookings_states.in_work",
  "Отказ клиента": "bookings_states.decline",
  "Нет машин": "bookings_states.no_cars",
  "Недозвон": "bookings_states.abandoned",
  "Ожидает ответа клиента": "bookings_states.wait_answer",
  "Ожидает оплаты": "bookings_states.wait_pay",
  "Активная": "bookings_states.active",
  "Отмена": "bookings_states.cancel",
  "Отъездила": "bookings_states.rided",
  "Вернуть залог": "bookings_states.return_deposit",
  "Подтверждена": "bookings_states.accepted",
  "Не подтверждена": "bookings_states.not_accepted",
  "Другое": "bookings_states.other",
};

// Статусы долгов, штрафов и счетов: значение из БД (по-русски) → ключ локали.
// Ровно тот же случай, что и статусы броней выше, — в базе они лежат русскими
// строками, а карта «значение → ключ» была скопирована по компонентам в трёх
// разных видах, из-за чего кнопки статуса в карточке клиента и в счетах брони
// остались непереведёнными: они печатали значение из БД как есть.
const DEBT_STATE_KEYS = {
  "Создан": "debts_state.created",
  "Не оплачен": "debts_state.don_paid",
  "Оповещён": "debts_state.notify",
  "Оплачен": "debts_state.paid",
  "Возмещён": "debts_state.refund",
  "Напомнить": "debts_state.remind",
};

const FINE_STATE_KEYS = {
  "Создан": "fines_state.created",
  // в базе встречается вариант с латинской «c» — он был отдельной веткой в Fines.vue
  "cоздан": "fines_state.created",
  "Оповещён": "fines_state.notify",
  "Оплачен": "fines_state.paid",
  "Возмещён": "fines_state.refund",
};

const INVOICE_STATE_KEYS = {
  "Создан": "invoice_state.created",
  "Выставлен": "invoice_state.send",
  "Частично оплачен": "invoice_state.paid_part",
  "Оплачен": "invoice_state.paid",
};

export function debtStateKey(state) {
  return DEBT_STATE_KEYS[state] || null;
}

export function fineStateKey(state) {
  return FINE_STATE_KEYS[state] || null;
}

export function invoiceStateKey(state) {
  return INVOICE_STATE_KEYS[state] || null;
}

export function bookingStateKey(state) {
  return BOOKING_STATE_KEYS[state] || null;
}

/**
 * Перевод статуса брони для шаблонов: $t по известному ключу, незнакомые
 * значения возвращаются как есть (кастомные статусы компаний — тоже данные).
 * @param {(key: string) => string} t — this.$t компонента
 */
export function translateBookingState(t, state) {
  const key = bookingStateKey(state);
  return key ? t(key) : state;
}

/**
 * Те же переводчики для долгов, штрафов и счетов. Незнакомое значение
 * возвращается как есть: компании заводят свои статусы, и прежние копии
 * отдавали на них «???» или пустую строку — кнопка получалась без подписи.
 * @param {(key: string) => string} t — this.$t компонента
 */
export function translateDebtState(t, state) {
  const key = debtStateKey(state);
  return key ? t(key) : state;
}

export function translateFineState(t, state) {
  const key = fineStateKey(state);
  return key ? t(key) : state;
}

export function translateInvoiceState(t, state) {
  const key = invoiceStateKey(state);
  return key ? t(key) : state;
}
